import { type FC, useState } from "react";
import { useMemo } from "react";

import Button from "@/design-system/button";
import Carousel from "@/design-system/carousel";
import { useViewportInfoContext } from "@/src/contexts";
import { type Device } from "@Components/Modules/ViewportResize";
import type { IOnStickyEvent } from "@Components/StickTopPanel";
import StickTopPanel from "@Components/StickTopPanel";
import * as stylex from "@stylexjs/stylex";

import { colors } from "../../styles/tokens.stylex";

import * as css from "./AlphabetList.module.css";

export interface IAlphabetList {
    allBrandsTitle?: string;
    groupKeys: string[];
    minFullWidthDevice: keyof typeof Device;
    className?: string;
    onAnchorClick: (text: string) => void;
}

export const defaultAlphabetFilter = "*";

const alphabetArray = [
    "#",
    "A",
    "B",
    "C",
    "D",
    "E",
    "F",
    "G",
    "H",
    "I",
    "J",
    "K",
    "L",
    "M",
    "N",
    "O",
    "P",
    "Q",
    "R",
    "S",
    "T",
    "U",
    "V",
    "W",
    "X",
    "Y",
    "Z",
];

const AlphabetList: FC<IAlphabetList> = (props) => {
    const { isMobileOrTablet } = useViewportInfoContext();

    const [carouselFixed, setCarouselFixed] = useState<boolean>(false);

    const lowercaseTitle = props.allBrandsTitle?.toLocaleUpperCase();

    const mainAlphabetList = props.allBrandsTitle ? [lowercaseTitle, ...alphabetArray] : alphabetArray;

    const toggleCarouselStick = (stickEvent: IOnStickyEvent): void => {
        if (stickEvent && stickEvent.sticky !== carouselFixed) {
            setCarouselFixed(stickEvent.sticky);
        }
    };

    const brandExistsInList = (anchor: string): boolean => {
        if (props.groupKeys.length === 0) {
            return false;
        }

        if (anchor === lowercaseTitle) {
            return true;
        }

        return props.groupKeys.findIndex((s) => s === anchor) >= 0;
    };

    const anchorClick = (text: string) => {
        if (props.onAnchorClick) {
            if (text === lowercaseTitle) {
                props.onAnchorClick(defaultAlphabetFilter);
                return;
            }
            props.onAnchorClick(text);
        }
    };

    const buildAnchorItem = (text: string, index: number) => {
        const isDisabled: boolean = !brandExistsInList(text);

        return (
            <Button
                key={index}
                as="button"
                size="None"
                variant="transparent"
                styleX={[
                    styles.anchorBtn,
                    index === mainAlphabetList.length - 1 && styles.noBorder,
                    index === 0 && styles.noPaddingLeft,
                ]}
                disabled={isDisabled}
                onClick={isDisabled ? undefined : () => anchorClick(text)}
            >
                {text}
            </Button>
        );
    };

    const alphabetList = useMemo(() => mainAlphabetList.map(buildAnchorItem), [props.groupKeys?.length]);

    return (
        <div className={css.alphabetListComponent}>
            <StickTopPanel
                onSticky={toggleCarouselStick}
                stickElementClassName={css.alphabetListComponent}
                fitToContainer
            >
                <div className={`${css.anchors} ${css.carousel}`}>
                    <Carousel
                        styleX={styles.carouselOverride}
                        showArrows={carouselFixed ? false : !isMobileOrTablet}
                        fullWidth
                    >
                        {alphabetList}
                    </Carousel>
                </div>
            </StickTopPanel>
        </div>
    );
};

const styles = stylex.create({
    carouselOverride: {
        gap: 0,
        gridAutoColumns: "unset",
    },

    noBorder: {
        borderRightStyle: "none",
    },

    anchorBtn: {
        borderRadius: 0,
        color: { default: colors.fg_Accent, ":disabled": colors.fg_Disabled },
        textAlign: "center",
        textTransform: "uppercase",
        borderRightColor: colors.fg_Tertiary,
        borderRightStyle: "solid",
        borderRightWidth: "1px",
        paddingBottom: "0",
        paddingLeft: "10px",
        paddingRight: "10px",
        paddingTop: "0",
        width: "100%",
    },

    noPaddingLeft: {
        paddingLeft: 0,
    },
});

export default AlphabetList;
